import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaUniversity, FaArrowRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import styles from "./Partners.module.css";
import { BaseURL, testUrl } from "../BaseData";

const Partners = () => {
    const { t, i18n } = useTranslation();
    const [partners, setPartners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [coverPaused, setCoverPaused] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [partnersRes, categoriesRes] = await Promise.all([
                    fetch(testUrl + "/api/partners/"),
                    fetch(testUrl + "/api/categoriespartnerships/")
                ]);
                const partnersData = await partnersRes.json();
                const categoriesData = await categoriesRes.json();
                setPartners(partnersData);
                setCategories(categoriesData);
                if (categoriesData.length > 0) {
                    setActiveCategory(categoriesData[0].id.toString());
                }
            } catch (error) {
                setError("Error fetching data");
                console.error("Error fetching data: ", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [i18n.language]);

    const filteredPartners = activeCategory === null
        ? partners
        : partners.filter(p => p.category === parseInt(activeCategory));

    const getName = (cat) => {
        const lang = i18n.language;
        if (lang === 'uz') return cat.name_uz;
        if (lang === 'ru') return cat.name_ru;
        return cat.name_en || cat.name_uz;
    };

    // 3D coverflow carousel once there are more than two partners.
    const partnerCount = Array.isArray(filteredPartners) ? filteredPartners.length : 0;
    const shouldCoverflow = partnerCount > 2;

    // Reset to the first card whenever the category (list) changes.
    useEffect(() => {
        setActiveIndex(0);
    }, [activeCategory]);

    // Auto-advance the coverflow; pauses on hover.
    useEffect(() => {
        if (!shouldCoverflow || coverPaused) return;
        const id = setInterval(() => {
            setActiveIndex((p) => (p + 1) % partnerCount);
        }, 3200);
        return () => clearInterval(id);
    }, [shouldCoverflow, coverPaused, partnerCount]);

    const renderPartnerCard = (partner, keyPrefix) => (
        <div key={`${keyPrefix}-${partner.id}`} className={styles.facultyCard}>
            <img
                src={partner.image || "/default-image.jpg"}
                alt={partner.name}
                className={styles.facultyImage}
            />
            <div className={styles.facultyInfo}>
                <h3>{partner.name}</h3>
            </div>
            <div className={styles.viewMore}>
                <a href={partner.link_url} target="_blank" rel="noopener noreferrer">
                    {t("see_more")}
                </a>
            </div>
        </div>
    );

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>Loading...</div>;
    if (error) return <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>{error}</div>;

    return (
        <div className={styles.mainContainer}>
            <div className={styles.container}>

                <div className={styles.section}>
                    <h3 className={styles.title}>{t("partners")}</h3>

                    <div className={styles.tabsContainer}>
                        <div className={styles.tabs}>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={`${styles.tab} ${activeCategory === cat.id.toString() ? styles.active : ""}`}
                                    onClick={() => setActiveCategory(cat.id.toString())}
                                >
                                    {getName(cat)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {partnerCount > 0 ? (
                        shouldCoverflow ? (
                            <div
                                className={styles.coverflow}
                                onMouseEnter={() => setCoverPaused(true)}
                                onMouseLeave={() => setCoverPaused(false)}
                            >
                                <button
                                    type="button"
                                    className={`${styles.cfNav} ${styles.cfPrev}`}
                                    onClick={() => setActiveIndex((p) => (p - 1 + partnerCount) % partnerCount)}
                                    aria-label="Oldingi"
                                >
                                    ‹
                                </button>

                                <div className={styles.cfStage} key={activeCategory}>
                                    {filteredPartners.filter(Boolean).map((partner, i) => {
                                        // Circular offset from the focused card (shortest way round).
                                        let off = i - activeIndex;
                                        if (off > partnerCount / 2) off -= partnerCount;
                                        if (off < -partnerCount / 2) off += partnerCount;
                                        const abs = Math.abs(off);
                                        const visible = abs <= 2;
                                        const ry = off < 0 ? 40 : off > 0 ? -40 : 0;
                                        const cardStyle = {
                                            transform: `translate(-50%, -50%) translateX(${off * 220}px) translateZ(${-abs * 140}px) rotateY(${ry}deg) scale(${off === 0 ? 1 : 0.82})`,
                                            opacity: abs === 0 ? 1 : abs === 1 ? 0.8 : abs === 2 ? 0.4 : 0,
                                            zIndex: 100 - abs,
                                            pointerEvents: visible ? "auto" : "none",
                                        };
                                        return (
                                            <div
                                                key={partner.id}
                                                className={styles.cfCard}
                                                style={cardStyle}
                                                onClick={() => off !== 0 && setActiveIndex(i)}
                                            >
                                                {renderPartnerCard(partner, "cf")}
                                            </div>
                                        );
                                    })}
                                </div>

                                <button
                                    type="button"
                                    className={`${styles.cfNav} ${styles.cfNext}`}
                                    onClick={() => setActiveIndex((p) => (p + 1) % partnerCount)}
                                    aria-label="Keyingi"
                                >
                                    ›
                                </button>
                            </div>
                        ) : (
                            <div className={styles.facultyList}>
                                {filteredPartners.filter(Boolean).map((p) => renderPartnerCard(p, 'a'))}
                            </div>
                        )
                    ) : (
                        <div className={styles.noPartners}>{t("no_partners_found")}</div>
                    )}
                </div>

                {/* Departments Section */}
                {/* <div className={styles.section}>
                    <h3 className={styles.title}>{t("departments")}</h3>
                    <div className={styles.facultyList}>
                        {departments.length > 0 ? (
                            departments.filter(Boolean).map((department) => (
                                <Link
                                    key={department.id}
                                    to={`/faculty-kafedra/${department.slug}`}
                                    className={styles.facultyCard}
                                >
                                    <img
                                        src={department.faculty?.image || "/default-image.jpg"}
                                        alt={
                                            getDepartmentName(department) || t("no_name_available")
                                        }
                                        className={styles.facultyImage}
                                    />
                                    <div className={styles.facultyInfo}>
                                        <h3>{getDepartmentName(department)}</h3>
                                        <FaUniversity className={styles.icon}/>
                                    </div>
                                    <div className={styles.viewMore}>
                                        <span>{t("see_more")}</span>
                                        <FaArrowRight/>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div>{t("no_departments_found")}</div>
                        )}
                    </div>
                </div> */}
            </div>
        </div>
    );
};

export default Partners;
